import React, { useState } from "react";
import AbGridOwl from "./AbGridOwl";
import { abOwlItems, splitAbjCards } from "./abHelpers";
import CardImg from "../../../components/CardImg";

const IMG = `${import.meta.env.BASE_URL}img/cards`;

function Slider({ id, list, margin, resetKey }) {
  return (
    <AbGridOwl
      id={id}
      className="ab-slider owl-carousel owl-theme"
      items={abOwlItems()}
      margin={margin}
      slideBy={4}
      resetKey={resetKey}
    >
      {list.map((c, i) => (
        <div key={`${i}-${c}`} className="item">
          <span>
            <CardImg src={`${IMG}/${c}.png`} alt="" />
          </span>
        </div>
      ))}
    </AbGridOwl>
  );
}

const splitList = (v) => (v ? String(v).split(",").reverse() : []);

/**
 * Andar Bahar (ab20) video card sliders, reference markup (inside .casino-video):
 *   .casino-video-cards[.hide-cards] > .casino-cards-shuffle + .casino-video-cards-container
 *     > #andarSlider + #baharSlider (Owl: rtl, margin 0, slideBy 4)
 * Slides: feed `aall` / `ball` split on "," and reversed (newest first).
 * Hidden (.hide-cards) when toggled off or there is no Bahar card yet; the toggle only works with Bahar cards.
 */
export function Ab20VideoCards({ feed }) {
  const [showCards, setShowCards] = useState(true);
  const andar = splitList(feed?.aall);
  const bahar = splitList(feed?.ball);
  const key = `${feed?.aall || ""}|${feed?.ball || ""}`;
  const hidden = !showCards || bahar.length === 0;
  if (bahar.length === 0 && !showCards) setShowCards(true); // new round re-opens a hand-closed strip
  return (
    <div className={`casino-video-cards${hidden ? " hide-cards" : ""}`}>
      <div className="casino-cards-shuffle" onClick={() => bahar.length > 0 && setShowCards((v) => !v)}>
        <i className="fas fa-grip-lines-vertical" />
      </div>{" "}
      <div className="casino-video-cards-container">
        <Slider id="andarSlider" list={andar} margin={0} resetKey={key} />{" "}
        <Slider id="baharSlider" list={bahar} margin={0} resetKey={key} />
      </div>
    </div>
  );
}

const labels = (firstClass) => (
  <div className="col-1">
    <div className="row row5">
      <div className={firstClass}>
        <b>A</b>
      </div>
    </div>{" "}
    <div className="row row5">
      <div className="col-12">
        <b>B</b>
      </div>
    </div>
  </div>
);

/**
 * Andar Bahar 2 (abj) card strip, reference markup.
 * variant "video" (inside .casino-video, once the feed has cards):
 *   .casino-video-cards.d-none-small[.hide-cards] > .casino-cards-shuffle + .casino-video-cards-container
 *     > .row.row5.align-items-center > .col-1 (A / B) + .col-2 (joker img.card-right)
 *       + .col-9 > 2 × .card-inner > .row.row5 > .col-3 (first card) + .col-9 > #andarSlider / #baharSlider
 *   (Owl: rtl, margin 10, slideBy 4). Hidden while toggled off or before the joker is dealt.
 * variant "detail" (reference IS_MOBILE, first child of .casino-detail): .casino-video-cards.d-none-desktop,
 *   same grid with #andarSliderMob / #baharSliderMob, which the reference never initialises (bare divs).
 */
export function AbjVideoCards({ cardString, variant = "video" }) {
  const [showCards, setShowCards] = useState(true);
  const { cards, andar, bahar } = splitAbjCards(cardString);
  const key = cardString || "";
  const img = (c, cls) => <CardImg src={`${IMG}/${c}.png`} className={cls} alt="" />;

  if (variant === "detail") {
    return (
      <div className="casino-video-cards d-none-desktop">
        <div className="casino-video-cards-container">
          <div className="row row5 align-items-center">
            {labels("col-12")}{" "}
            <div className="col-2">
              <span>{img(cards[0], "card-right")}</span>
            </div>{" "}
            <div className="col-9">
              <div className="card-inner">
                <div className="row row5">
                  <div className="col-3">
                    <span>{img(cards[2])}</span>
                  </div>{" "}
                  <div className="col-9">
                    <div id="andarSliderMob" className="ab-slider owl-carousel owl-theme" />
                  </div>
                </div>
              </div>{" "}
              <div className="card-inner">
                <div className="row row5 mt-1">
                  <div className="col-3">
                    <span>{img(cards[1])}</span>
                  </div>{" "}
                  <div className="col-9">
                    <div id="baharSliderMob" className="ab-slider owl-carousel owl-theme" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const hidden = !showCards || cards[0] === "1";
  if (cards[0] === "1" && !showCards) setShowCards(true); // new round re-opens a hand-closed strip
  return (
    <div className={`casino-video-cards d-none-small${hidden ? " hide-cards" : ""}`}>
      <div className="casino-cards-shuffle" onClick={() => cards[0] !== "1" && setShowCards((v) => !v)}>
        <i className="fas fa-grip-lines-vertical" />
      </div>{" "}
      <div className="casino-video-cards-container">
        <div className="row row5 align-items-center">
          {labels("col-12 mb-3")}{" "}
          <div className="col-2">
            <span>{img(cards[0], "card-right")}</span>
          </div>{" "}
          <div className="col-9">
            <div className="card-inner mb-1">
              <div className="row row5">
                <div className="col-3">
                  <span>{img(cards[2])}</span>
                </div>{" "}
                <div className="col-9">
                  <Slider id="andarSlider" list={andar} margin={10} resetKey={key} />
                </div>
              </div>
            </div>{" "}
            <div className="card-inner">
              <div className="row row5">
                <div className="col-3">
                  <span>{img(cards[1])}</span>
                </div>{" "}
                <div className="col-9">
                  <Slider id="baharSlider" list={bahar} margin={10} resetKey={key} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
